import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import type { CSSProperties } from "react";

type ProcessorType = "unit" | "direct" | "custom" | "skipped";

interface BaseProperty {
  cssProperty: keyof CSSProperties;
  processorType: ProcessorType;
}

interface UnitProperty extends BaseProperty {
  cssProperty:
    | "fontSize"
    | "lineHeight"
    | "letterSpacing"
    | "borderWidth"
    | "borderTopWidth"
    | "borderRightWidth"
    | "borderBottomWidth"
    | "borderLeftWidth"
    | "opacity"
    | "gap"
    | "rowGap"
    | "columnGap";
  defaultUnit: string;
  processorType: "unit";
}

interface DirectProperty extends BaseProperty {
  cssProperty:
    | "color"
    | "fontFamily"
    | "textAlign"
    | "textTransform"
    | "textDecoration"
    | "fontStyle"
    | "borderColor"
    | "borderStyle"
    | "backgroundColor"
    | "width"
    | "minWidth"
    | "maxWidth"
    | "minHeight"
    | "maxHeight"
    | "height"
    | "listStyleType"
    | "listStylePosition"
    | "objectFit"
    | "objectPosition"
    | "backgroundSize"
    | "display"
    | "flexDirection"
    | "alignItems"
    | "justifyContent"
    | "flexWrap"
    | "visibility"
    | "position"
    | "gridTemplateColumns"
    | "gridTemplateRows";
  processorType: "direct";
}

interface CustomProperty extends BaseProperty {
  cssProperty:
    | "fontWeight"
    | "borderRadius"
    | "padding"
    | "margin"
    | "backgroundImage";
  processor: (value: StyleValue) => string | number | undefined;
  processorType: "custom";
}

/**
 *   Skipped properties are used to mark custom style properties for certain blocks
 *   that are not part of standard CSS, and for which processing step can be skipped.
 *   These properties are handled directly by the block itself.
 */
interface SkippedProperty {
  processorType: "skipped";
}

interface BorderRadiusValue {
  border_left_bottom_radius: number;
  border_left_top_radius: number;
  border_right_bottom_radius: number;
  border_right_top_radius: number;
}

export type StyleDefinition =
  | UnitProperty
  | DirectProperty
  | CustomProperty
  | SkippedProperty;

export const styleMap: Record<string, StyleDefinition> = {
  color: {
    cssProperty: "color",
    processorType: "direct",
  },
  fontFamily: {
    cssProperty: "fontFamily",
    processorType: "direct",
  },
  textTransform: {
    cssProperty: "textTransform",
    processorType: "direct",
  },
  fontStyle: {
    cssProperty: "fontStyle",
    processorType: "direct",
  },
  fontSize: {
    cssProperty: "fontSize",
    processorType: "unit",
    defaultUnit: "rem",
  },
  lineHeight: {
    cssProperty: "lineHeight",
    processorType: "unit",
    defaultUnit: "",
  },
  letterSpacing: {
    cssProperty: "letterSpacing",
    processorType: "unit",
    defaultUnit: "",
  },
  fontWeight: {
    cssProperty: "fontWeight",
    processorType: "custom",
    processor: value => {
      const val = parseInt(String(value.value), 10);
      return isNaN(val) ? String(value.value) : val;
    },
  },
  textAlign: {
    cssProperty: "textAlign",
    processorType: "direct",
  },
  textDecoration: {
    cssProperty: "textDecoration",
    processorType: "direct",
  },
  borderRadius: {
    cssProperty: "borderRadius",
    processorType: "custom",
    processor: value => {
      if (typeof value.value === "object") {
        const corners = value.value as BorderRadiusValue;

        return `${corners.border_left_top_radius}px ${corners.border_right_top_radius}px ${corners.border_right_bottom_radius}px ${corners.border_left_bottom_radius}px`;
      }
      return typeof value.value === "number" ? `${value.value}px` : undefined;
    },
  },
  borderWidth: {
    cssProperty: "borderWidth",
    processorType: "unit",
    defaultUnit: "px",
  },
  borderTopWidth: {
    cssProperty: "borderTopWidth",
    processorType: "unit",
    defaultUnit: "px",
  },
  borderColor: {
    cssProperty: "borderColor",
    processorType: "direct",
  },
  borderStyle: {
    cssProperty: "borderStyle",
    processorType: "direct",
  },
  backgroundColor: {
    cssProperty: "backgroundColor",
    processorType: "direct",
  },
  backgroundImage: {
    cssProperty: "backgroundImage",
    processorType: "custom",
    processor: value => {
      if (typeof value.value === "string" && Boolean(value.value)) {
        const val = value.value.trim();
        if (val.includes("gradient(")) {
          return val;
        }
        return `url(${val})`;
      }
      return undefined;
    },
  },
  padding: {
    cssProperty: "padding",
    processorType: "custom",
    processor: value => {
      const val = value.value as {
        paddingTop?: number | string;
        paddingBottom?: number | string;
        paddingLeft?: number | string;
        paddingRight?: number | string;
      };

      if (typeof value.value === "object") {
        const { paddingTop, paddingBottom, paddingLeft, paddingRight } = val;

        return `${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px`;
      }

      return undefined;
    },
  },
  margin: {
    cssProperty: "margin",
    processorType: "custom",
    processor: value => {
      const val = value.value as {
        marginTop?: number | string;
        marginBottom?: number | string;
        marginLeft?: number | string;
        marginRight?: number | string;
      };

      if (typeof value.value === "object") {
        const { marginTop, marginBottom, marginLeft, marginRight } = val;
        const withUnit = (side?: number | string) =>
          side === "auto" ? side : `${side}px`;

        return `${withUnit(marginTop)} ${withUnit(marginRight)} ${withUnit(marginBottom)} ${withUnit(marginLeft)}`;
      }

      return undefined;
    },
  },
  width: {
    cssProperty: "width",
    processorType: "direct",
  },
  height: {
    cssProperty: "height",
    processorType: "direct",
  },
  minWidth: {
    cssProperty: "minWidth",
    processorType: "direct",
  },
  maxWidth: {
    cssProperty: "maxWidth",
    processorType: "direct",
  },
  minHeight: {
    cssProperty: "minHeight",
    processorType: "direct",
  },
  maxHeight: {
    cssProperty: "maxHeight",
    processorType: "direct",
  },
  listStyleType: {
    cssProperty: "listStyleType",
    processorType: "direct",
  },
  listStylePosition: {
    cssProperty: "listStylePosition",
    processorType: "direct",
  },
  objectFit: {
    cssProperty: "objectFit",
    processorType: "direct",
  },
  backgroundSize: {
    cssProperty: "backgroundSize",
    processorType: "direct",
  },
  objectPosition: {
    cssProperty: "objectPosition",
    processorType: "direct",
  },
  opacity: {
    cssProperty: "opacity",
    processorType: "unit",
    defaultUnit: "%",
  },
  display: {
    cssProperty: "display",
    processorType: "direct",
  },
  flexDirection: {
    cssProperty: "flexDirection",
    processorType: "direct",
  },
  alignItems: {
    cssProperty: "alignItems",
    processorType: "direct",
  },
  justifyContent: {
    cssProperty: "justifyContent",
    processorType: "direct",
  },
  flexWrap: {
    cssProperty: "flexWrap",
    processorType: "direct",
  },
  gap: {
    cssProperty: "gap",
    processorType: "unit",
    defaultUnit: "px",
  },
  visibility: {
    cssProperty: "visibility",
    processorType: "direct",
  },
  rowGap: {
    cssProperty: "rowGap",
    processorType: "unit",
    defaultUnit: "px",
  },
  columnGap: {
    cssProperty: "columnGap",
    processorType: "unit",
    defaultUnit: "px",
  },
  position: {
    cssProperty: "position",
    processorType: "direct",
  },
  gridTemplateColumns: {
    cssProperty: "gridTemplateColumns",
    processorType: "direct",
  },
  gridTemplateRows: {
    cssProperty: "gridTemplateRows",
    processorType: "direct",
  },
  sizing: {
    processorType: "skipped",
  },
} as const;
